import { css, styled } from 'styled-components'
import { onMediumScreen } from 'mixtiles-web-common'
import { TILE_TOGGLE_DURATION_MS } from './UpsellWallTile.styles'

const DESKTOP_MAX_TILE_PX = 136
const TOGGLE_ICON_SIZE_PX = 28

// A size container, so the grid can fit both the width and the height the sheet leaves it.
export const WallScene = styled.div`
  display: flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 24px;
  container-type: size;
`

// The widest grid whose square cells still fit the scene's height: rows of cells plus the gaps between them.
export const TilesGrid = styled.div`
  --wall-gap: clamp(12px, 5cqmin, 32px);
  display: grid;
  grid-template-columns: repeat(var(--wall-columns), 1fr);
  gap: var(--wall-gap);
  width: min(
    100cqw,
    calc(
      (100cqh - (var(--wall-rows) - 1) * var(--wall-gap)) * var(--wall-columns) /
        var(--wall-rows) + (var(--wall-columns) - 1) * var(--wall-gap)
    )
  );

  ${onMediumScreen()} {
    max-width: calc(
      var(--wall-columns) * ${DESKTOP_MAX_TILE_PX}px + (var(--wall-columns) - 1) *
        var(--wall-gap)
    );
  }
`

// A square cell fits any tile orientation: UpsellWallTile sizes itself inside its box.
const tileSlotLayout = css`
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  min-width: 0;
`

export const TileSlot = styled.div`
  ${tileSlotLayout}
`

export const TileToggleSlot = styled.button`
  ${tileSlotLayout}
  position: relative;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`

// The design puts the circle 11px above and 8px past the tile's top-right corner.
// Above the tiles: on a phone the next tile's shadow reaches under it, and iOS Safari lifts a shadow painted over
// the rotating bar into its own layer, where it draws the shadow and frame blank.
export const ToggleIcon = styled.span`
  position: absolute;
  z-index: 1;
  top: -11px;
  right: -8px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${TOGGLE_ICON_SIZE_PX}px;
  height: ${TOGGLE_ICON_SIZE_PX}px;
  border: 1px solid ${({ theme }) => theme.colors.grayMedium};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow:
    0 3px 4px rgba(43, 5, 20, 0.07),
    0 4px 13px rgba(43, 5, 20, 0.04);
  transition:
    background-color ${TILE_TOGGLE_DURATION_MS}ms,
    border-color ${TILE_TOGGLE_DURATION_MS}ms,
    box-shadow ${TILE_TOGGLE_DURATION_MS}ms;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${TileToggleSlot}[aria-pressed='false'] & {
    border-color: transparent;
    background-color: ${({ theme }) => theme.colors.grayMidDark};
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.03);
  }
`

export const ToggleBar = styled.span`
  position: absolute;
  width: 14px;
  height: 3px;
  border-radius: 2px;
  background-color: ${({ theme }) => theme.colors.grayDark};
  transition:
    background-color ${TILE_TOGGLE_DURATION_MS}ms,
    transform ${TILE_TOGGLE_DURATION_MS}ms;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${TileToggleSlot}[aria-pressed='false'] & {
    background-color: ${({ theme }) => theme.colors.grayAccent};
  }
`

// Lies on the minus bar while the tile is on, and turns upright into a plus when it's off.
export const ToggleCrossBar = styled(ToggleBar)`
  ${TileToggleSlot}[aria-pressed='false'] & {
    transform: rotate(90deg);
  }
`
